// ============================================
// COTIZACIONPDF.JS — PDF PREMIUM de la cotizacion para el CLIENTE (3 secciones)
// ============================================
// jsPDF + jspdf-autotable. Sin html2canvas. Tipografia Poppins (TTF en /fonts, se carga al
// generar; si falla, Helvetica). CLIENTE-FACING: NUNCA muestra costo, margen, desglose
// interno ni "SYSCOM". Los NUMEROS no se tocan aqui: llegan calculados y se imprimen.
//
// SECCION 1 — COTIZACION: banda superior con degradado institucional (logo + emisor en blanco;
//   "PROPUESTA COMERCIAL", FOLIO protagonista, chip de vigencia), etiqueta PROYECTO + titulo,
//   3 tarjetas (Cliente / Atiende / Fecha), tabla elegante (encabezado azul con esquinas
//   redondeadas, filas alternadas, chips de marca, filas de SERVICIO BEEP en verde) y la
//   pagina de totales: RESUMEN DE LA PROPUESTA, NOTAS (ambar), Incluye / No incluye y la
//   tarjeta INVERSION TOTAL con degradado + IMPORTE EN LETRA. Pie con el asesor. Al final,
//   TERMINOS Y CONDICIONES en lista (la foto guardada en la cotizacion; fluye a la pagina
//   siguiente con el encabezado repetido, sin recortes).
// SECCION 2 — CONDICIONES Y DATOS DE PAGO: 3 tarjetas con dato grande e icono (forma de pago,
//   entrega, vigencia), "tarjeta de banco" oscura por cuenta (monoespaciada), terminos en dos
//   columnas (check / equis) y ACEPTACION (nombre, firma y fecha del CLIENTE).
// SECCION 3 — CERTIFICADO DE GARANTIA: cabecera tipo diploma con sello integrado, 2 tarjetas
//   de cobertura (equipos / instalacion Beep), la MISMA tabla sin precios (garantia como chip
//   y Vence), que cubre, 8 casos no cubiertos en cuadricula compacta, visita de diagnostico y
//   cierre con codigo de verificacion + QR (con logo, nivel H: lo arma el Previsualizador).
// CAPA 4: marca de agua con el ISOTIPO (6% de opacidad) en todas las paginas + microtexto.
//
// Fotos via proxy del backend (/api/cotizaciones/img-proxy). Datos del emisor, cuentas y textos
// desde GET /api/empresa (nunca inventados; si faltan, se omiten con elegancia).

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_BASE_CLI } from '../components/helpersClientes';
import { TASA_IVA } from '../config/iva';
import { importeALetras } from './numeroALetras';

// Paleta institucional (hex: las CSS-vars no sirven en jsPDF).
const C = {
  navy: '#0F3A6B', blue: '#2E7AC4', blue700: '#265F97', blue500: '#5E99D2', blue300: '#9EC2E5', blue100: '#DEEBF7', blue50: '#F3F8FD',
  ink: '#1F2A37', gray700: '#3D4248', gray500: '#5E666E', gray400: '#8A9199', gray200: '#E3E6EA', gray100: '#F3F5F8', gray50: '#F8FAFC',
  blanco: '#FFFFFF',
  verdeSuave: '#EAF7F0', verde700: '#1E7A4C', verde100: '#D3F0DF',
  ambarSuave: '#FFF6E5', ambar700: '#9A5B00', ambarBorde: '#F3D9A4',
  rojoSuave: '#FDF2F2', rojo700: '#B42318', rojoBorde: '#F5C6C2',
  cardDark1: '#1C2A3A', cardDark2: '#0F3A6B',
};

// EMISOR por default (solo si GET /api/empresa no responde). Los datos fiscales viven en
// Administracion > Datos de la empresa; aqui NO se inventan.
export const EMISOR = { razonSocial: 'Comercializadora Beep México', rfc: '', direccion: '', telefono: '', correo: '', web: 'beepmx.com' };

// PORTADA-EXP: LOGO_URL y cargarLogo se reusan en portadaExpedientePDF.js.
export const LOGO_URL = '/Logo%20Horizontal%20Original.png';
export const LOGO_BLANCO_URL = '/Logo%20Horizontal%20blanco.png';
export const ISOTIPO_URL = '/isotipo-beep.png';
const FUENTES = [
  ['Poppins-Regular.ttf', 'normal'],
  ['Poppins-SemiBold.ttf', 'bold'],
  ['Poppins-Bold.ttf', 'black'],
];

// ---------- formato ----------
function fmtMonto(n) {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (v < 0 ? '-' : '') + '$' + s;
}
function fmtFecha(v) {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
function fmtFechaLarga(v) {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '—';
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}
export function fmtGarantia(meses, dias) {
  if (dias && !meses) return `${dias} días`;
  const m = Number(meses) || 0;
  if (!m) return '—';
  if (m % 12 === 0) { const a = m / 12; return a === 1 ? '1 año' : `${a} años`; }
  return m === 1 ? '1 mes' : `${m} meses`;
}

// ---------- recursos (imagenes / fuentes) ----------
// NOTA: todo addImage lleva alias (una copia por imagen aunque se pinte en N paginas) y
// compresion 'FAST' (jsPDF guarda los PNG en crudo si no se le pide comprimir: MB de mas).
function dimsDeDataUrl(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch { resolve(null); }
  });
}
export function cargarLogo(url, maxPx = 0) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
          // maxPx: lado mayor maximo (los logos de marca vienen a 1667 px; en el PDF pesan MB si no se reducen)
          const ratio = maxPx > 0 ? Math.min(1, maxPx / Math.max(nw, nh)) : 1;
          canvas.width = Math.max(1, Math.round(nw * ratio));
          canvas.height = Math.max(1, Math.round(nh * ratio));
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve({ dataUrl: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch { resolve(null); }
  });
}
function recodificarAPng(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width || 1;
          canvas.height = img.naturalHeight || img.height || 1;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch { resolve(null); }
  });
}
// Reduce una imagen a maxPx de lado mayor sobre fondo blanco y la entrega como JPEG (calidad 0.86).
function reducirImagen(dataUrl, dim, maxPx) {
  return new Promise((resolve) => {
    try {
      const ratio = Math.min(1, maxPx / Math.max(dim.w, dim.h));
      const w = Math.max(1, Math.round(dim.w * ratio)), h = Math.max(1, Math.round(dim.h * ratio));
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL('image/jpeg', 0.86);
          resolve(out && out.startsWith('data:image/jpeg') ? { dataUrl: out, fmt: 'JPEG', w, h } : null);
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch { resolve(null); }
  });
}

async function cargarImagenesLineas(lineas) {
  const mapa = {};
  await Promise.all((lineas || []).map(async (l, i) => {
    const url = l && l.imagen_url;
    if (!url) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/img-proxy?u=${encodeURIComponent(url)}`);
      if (!r.ok) return;
      const j = await r.json();
      if (!j || !j.ok || !j.dataUrl) return;
      let dataUrl = j.dataUrl;
      let fmt = (j.mime === 'image/png') ? 'PNG' : (j.mime === 'image/jpeg') ? 'JPEG' : null;
      if (!fmt) { dataUrl = await recodificarAPng(dataUrl); if (!dataUrl) return; fmt = 'PNG'; }
      let dim = await dimsDeDataUrl(dataUrl);
      if (!dim) return;
      // Miniatura: la celda mide ~16 mm; 360 px sobran para imprimir nitido y el PDF pesa 20x menos.
      const red = await reducirImagen(dataUrl, dim, 360);
      if (red) { dataUrl = red.dataUrl; fmt = red.fmt; dim = { w: red.w, h: red.h }; }
      mapa[i] = { dataUrl, w: dim.w, h: dim.h, fmt };
    } catch { /* sin imagen */ }
  }));
  return mapa;
}
function bufferABase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
// Registra Poppins en el documento. Devuelve el nombre de familia a usar ('Poppins' o 'helvetica').
async function cargarFuentes(doc) {
  try {
    const cargadas = await Promise.all(FUENTES.map(async ([archivo]) => {
      const r = await fetch(`/fonts/${archivo}`);
      if (!r.ok) throw new Error(archivo);
      return bufferABase64(await r.arrayBuffer());
    }));
    FUENTES.forEach(([archivo, estilo], i) => { doc.addFileToVFS(archivo, cargadas[i]); doc.addFont(archivo, 'Poppins', estilo); });
    return 'Poppins';
  } catch { return 'helvetica'; }
}

// ---------- nombre corto / detalle (FIX 21) ----------
// Corta en un separador natural (" / ", " | ", " · ", ", ") sin partir dentro de parentesis y
// sin dejar un numero o unidad huerfano al final del nombre corto. Si no hay separador
// razonable, NO se parte (el nombre corto envuelve en 2 lineas).
const UNIDADES_RX = /^(cm|mm|m|mts|mt|km|w|v|kv|a|ah|db|kg|g|gb|tb|mb|mp|hz|ghz|mhz|px|pulg|in|ft|lts|l|°|%|x)$/i;
// Palabras que no pueden cerrar el nombre corto (dejarian la frase colgando)
const COLGANTES_RX = /^(de|del|con|para|por|y|o|u|e|el|la|los|las|en|a|al|sin|sobre|un|una|tipo|cal\.?)$/i;
// PDF-DESCRIPCION: repaso de limpieza en el cliente (el servidor ya la guarda limpia): sin etiquetas HTML, sin
// entidades y sin caracteres que la fuente del PDF no tenga. Devuelve una sola frase.
export function textoDescripcion(valor) {
  if (valor == null) return '';
  return String(valor)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#39;/g, "'")
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/[\u2013\u2014\u2022]/g, '-').replace(/[\u201c\u201d]/g, '"').replace(/[\u2018\u2019]/g, "'").replace(/\u2026/g, '...')
    .replace(/[^\x20-\x7E\u00A1-\u00FF]/g, ' ')
    .replace(/\s+/g, ' ').trim();
}

export function partirNombre(nombre) {
  const s = String(nombre || '').replace(/\s+/g, ' ').trim();
  if (!s) return { corto: '', detalle: '' };
  const SEPS = [' / ', ' | ', ' · ', ', ', '; '];
  let depth = 0, mejor = -1, mejorLen = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    if (depth) continue;
    for (const sep of SEPS) {
      if (s.startsWith(sep, i) && i >= 14 && i <= 72) {
        const antes = s.slice(0, i).trim();
        const ult = antes.split(' ').pop();
        // no dejar numero / unidad huerfana ("... con 5" o "... 17 cm")
        if (/^\d[\d.,]*$/.test(ult) || UNIDADES_RX.test(ult) || COLGANTES_RX.test(ult)) break;
        if ((antes.match(/\(/g) || []).length !== (antes.match(/\)/g) || []).length) break;
        if (mejor === -1) { mejor = i; mejorLen = sep.length; }
        break;
      }
    }
    if (mejor !== -1) break;
  }
  if (mejor !== -1) return { corto: s.slice(0, mejor).trim(), detalle: s.slice(mejor + mejorLen).trim() };
  if (s.length <= 95) return { corto: s, detalle: '' };
  // Muy largo y sin separador: cortar en espacio antes de 80, retrocediendo si queda huerfano.
  let corte = s.lastIndexOf(' ', 80);
  for (let k = 0; k < 6 && corte > 20; k++) {
    const antes = s.slice(0, corte).trim(); const ult = antes.split(' ').pop();
    const abiertos = (antes.match(/[([]/g) || []).length, cerrados = (antes.match(/[)\]]/g) || []).length;
    if (!/^\d[\d.,]*$/.test(ult) && !UNIDADES_RX.test(ult) && !COLGANTES_RX.test(ult) && abiertos === cerrados) break;
    corte = s.lastIndexOf(' ', corte - 1);
  }
  if (corte <= 20) return { corto: s, detalle: '' };
  return { corto: s.slice(0, corte).trim(), detalle: s.slice(corte).trim() };
}

// ---------- iconos vectoriales (estilo Lucide: trazo redondeado) ----------
// Caja s x s en (x, y). color = trazo. Nunca emojis.
export function dibujarIcono(doc, clave, x, y, s, color = C.blue700) {
  doc.setDrawColor(color); doc.setFillColor(color); doc.setLineWidth(Math.max(0.35, s * 0.075)); doc.setLineCap('round'); doc.setLineJoin('round');
  const L = (x1, y1, x2, y2) => doc.line(x + x1 * s, y + y1 * s, x + x2 * s, y + y2 * s);
  const R = (rx, ry, rw, rh, rr, st = 'S') => doc.roundedRect(x + rx * s, y + ry * s, rw * s, rh * s, rr * s, rr * s, st);
  const O = (cx, cy, r, st = 'S') => doc.circle(x + cx * s, y + cy * s, r * s, st);
  switch (clave) {
    case 'zap': L(0.55, 0.05, 0.25, 0.55); L(0.25, 0.55, 0.5, 0.55); L(0.5, 0.55, 0.42, 0.95); L(0.42, 0.95, 0.78, 0.42); L(0.78, 0.42, 0.52, 0.42); L(0.52, 0.42, 0.55, 0.05); break;
    case 'wrench': O(0.3, 0.3, 0.18); L(0.4, 0.42, 0.9, 0.92); L(0.46, 0.34, 0.94, 0.82); L(0.9, 0.92, 0.94, 0.82); L(0.18, 0.2, 0.3, 0.3); break;
    case 'droplets': L(0.5, 0.08, 0.24, 0.5); L(0.5, 0.08, 0.76, 0.5); doc.ellipse(x + 0.5 * s, y + 0.62 * s, 0.26 * s, 0.3 * s, 'S'); break;
    case 'shield-alert': L(0.5, 0.06, 0.16, 0.2); L(0.16, 0.2, 0.16, 0.5); L(0.16, 0.5, 0.5, 0.94); L(0.5, 0.94, 0.84, 0.5); L(0.84, 0.5, 0.84, 0.2); L(0.84, 0.2, 0.5, 0.06); L(0.5, 0.32, 0.5, 0.58); O(0.5, 0.72, 0.035, 'F'); break;
    case 'shield-check': L(0.5, 0.06, 0.16, 0.2); L(0.16, 0.2, 0.16, 0.5); L(0.16, 0.5, 0.5, 0.94); L(0.5, 0.94, 0.84, 0.5); L(0.84, 0.5, 0.84, 0.2); L(0.84, 0.2, 0.5, 0.06); L(0.34, 0.5, 0.46, 0.62); L(0.46, 0.62, 0.68, 0.38); break;
    case 'flame': L(0.5, 0.06, 0.26, 0.42); L(0.26, 0.42, 0.24, 0.66); doc.ellipse(x + 0.5 * s, y + 0.7 * s, 0.26 * s, 0.24 * s, 'S'); L(0.5, 0.06, 0.66, 0.36); L(0.66, 0.36, 0.76, 0.66); L(0.5, 0.5, 0.42, 0.68); L(0.5, 0.5, 0.58, 0.68); break;
    case 'bug': doc.ellipse(x + 0.5 * s, y + 0.6 * s, 0.2 * s, 0.28 * s, 'S'); O(0.5, 0.28, 0.1); L(0.3, 0.5, 0.1, 0.42); L(0.3, 0.64, 0.1, 0.64); L(0.3, 0.78, 0.12, 0.88); L(0.7, 0.5, 0.9, 0.42); L(0.7, 0.64, 0.9, 0.64); L(0.7, 0.78, 0.88, 0.88); L(0.44, 0.2, 0.36, 0.1); L(0.56, 0.2, 0.64, 0.1); break;
    case 'wifi-off': L(0.1, 0.4, 0.28, 0.28); L(0.72, 0.28, 0.9, 0.4); L(0.24, 0.56, 0.4, 0.46); L(0.6, 0.46, 0.76, 0.56); L(0.38, 0.72, 0.5, 0.64); L(0.5, 0.64, 0.62, 0.72); O(0.5, 0.86, 0.035, 'F'); L(0.14, 0.14, 0.86, 0.86); break;
    case 'battery-low': R(0.08, 0.3, 0.74, 0.4, 0.05); doc.rect(x + 0.84 * s, y + 0.42 * s, 0.08 * s, 0.16 * s, 'S'); doc.rect(x + 0.14 * s, y + 0.36 * s, 0.14 * s, 0.28 * s, 'F'); break;
    case 'credit-card': R(0.06, 0.2, 0.88, 0.6, 0.08); L(0.06, 0.38, 0.94, 0.38); L(0.2, 0.62, 0.4, 0.62); break;
    case 'truck': R(0.06, 0.28, 0.54, 0.4, 0.04); L(0.6, 0.38, 0.8, 0.38); L(0.8, 0.38, 0.94, 0.54); L(0.94, 0.54, 0.94, 0.68); L(0.6, 0.68, 0.94, 0.68); O(0.24, 0.74, 0.08); O(0.78, 0.74, 0.08); break;
    case 'calendar': R(0.1, 0.18, 0.8, 0.72, 0.08); L(0.1, 0.4, 0.9, 0.4); L(0.32, 0.08, 0.32, 0.28); L(0.68, 0.08, 0.68, 0.28); O(0.5, 0.64, 0.05, 'F'); break;
    case 'package': L(0.5, 0.1, 0.12, 0.3); L(0.12, 0.3, 0.12, 0.72); L(0.12, 0.72, 0.5, 0.92); L(0.5, 0.92, 0.88, 0.72); L(0.88, 0.72, 0.88, 0.3); L(0.88, 0.3, 0.5, 0.1); L(0.12, 0.3, 0.5, 0.5); L(0.5, 0.5, 0.88, 0.3); L(0.5, 0.5, 0.5, 0.92); break;
    case 'check': L(0.18, 0.52, 0.42, 0.76); L(0.42, 0.76, 0.84, 0.28); break;
    case 'x': L(0.24, 0.24, 0.76, 0.76); L(0.76, 0.24, 0.24, 0.76); break;
    case 'user': O(0.5, 0.32, 0.18); L(0.16, 0.9, 0.2, 0.74); L(0.2, 0.74, 0.8, 0.74); L(0.8, 0.74, 0.84, 0.9); break;
    case 'mail': R(0.06, 0.22, 0.88, 0.56, 0.06); L(0.06, 0.26, 0.5, 0.56); L(0.5, 0.56, 0.94, 0.26); break;
    case 'phone': R(0.3, 0.06, 0.4, 0.88, 0.1); L(0.44, 0.8, 0.56, 0.8); break;
    default: O(0.5, 0.5, 0.4); L(0.5, 0.28, 0.5, 0.56); O(0.5, 0.7, 0.035, 'F');
  }
}

// ---------- utilidades de dibujo ----------
function hexARgb(hex) { const h = hex.replace('#', ''); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; }
function mezcla(c1, c2, t) { const a = hexARgb(c1), b = hexARgb(c2); return a.map((v, i) => Math.round(v + (b[i] - v) * t)); }

// ============================================
// datos = { emisor, cliente:{nombre,sucursal,vendedor,vendedorCorreo}, cotizacion:{folio,fecha,vigencia,
//   nombre,nota,moneda,expedienteFolio,anticipoPct,saldoDias,tiempoEntrega,incluirPaginaPago,incluirCertificado,
//   terminos:[{titulo,texto}] (TERMINOS-COTIZACION; null/[] = cotizacion anterior, sin bloque)},
//   lineas:[...], totales:{subtotal,iva,total}, cuentas:[{banco,moneda,beneficiario,numero_cuenta,clabe,swift,sucursal}],
//   garantiaConfig:{que_cubre,apertura_no_cubiertos,visita_diagnostico,contacto,terminos_comerciales,
//     garantia_instalacion_meses, garantia_servicio_dias, correo_comprobantes, casos:[...]},
//   certificado:{folio,codigo,fecha_inicio,lineas:[{fecha_vencimiento}]}|null, qrDataUrl, urlVerificacion }
// ============================================
export async function generarCotizacionPDF(datos) {
  const emisor = (datos && datos.emisor) || EMISOR;
  const cliente = (datos && datos.cliente) || {};
  const cot = (datos && datos.cotizacion) || {};
  const lineas = Array.isArray(datos && datos.lineas) ? datos.lineas : [];
  const totales = (datos && datos.totales) || {};
  const cuentas = Array.isArray(datos && datos.cuentas) ? datos.cuentas : [];
  const gcfg = (datos && datos.garantiaConfig) || {};
  const cert = (datos && datos.certificado) || null;
  const incluirPago = cot.incluirPaginaPago !== false;
  const incluirCert = cot.incluirCertificado !== false;
  // PDF-DESCRIPCION (Ricardo 2026-10-30): la descripcion del producto debajo de su nombre, solo si la casilla de la
  // cotizacion esta prendida. Solo en la tabla de la cotizacion (el certificado sigue igual).
  const mostrarDescripcion = cot.mostrarDescripcion === true;
  const moneda = String(cot.moneda || 'MXN').toUpperCase() === 'USD' ? 'USD' : 'MXN';

  const doc = new jsPDF('p', 'mm', 'letter');
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 16; // margen
  const CW = W - M * 2; // ancho util

  // Recursos en paralelo: fuentes, logos, isotipo y fotos.
  const [fam, logoBlanco, isotipo, imgs] = await Promise.all([
    cargarFuentes(doc), cargarLogo(LOGO_BLANCO_URL, 700), cargarLogo(ISOTIPO_URL, 512), cargarImagenesLineas(lineas),
  ]);
  const F = (estilo = 'normal', size, color) => {
    let st = estilo;
    if (fam !== 'Poppins' && st === 'black') st = 'bold';
    doc.setFont(fam, st);
    if (size) doc.setFontSize(size);
    if (color) doc.setTextColor(color);
  };
  const MONO = (bold = false, size, color) => { doc.setFont('courier', bold ? 'bold' : 'normal'); if (size) doc.setFontSize(size); if (color) doc.setTextColor(color); };
  const opacidad = (fn, op) => {
    try { doc.saveGraphicsState(); doc.setGState(new doc.GState({ opacity: op })); fn(); doc.restoreGraphicsState(); }
    catch { /* sin transparencia */ }
  };

  // Degradado horizontal (o vertical) por tiras finas. Se recorta a un rectangulo redondeado.
  function degradado(x, y, w, h, c1, c2, r = 0, vertical = false, pasos = 56) {
    let clip = false;
    if (r > 0) {
      try { doc.saveGraphicsState(); doc.roundedRect(x, y, w, h, r, r, null); doc.clip(); doc.discardPath(); clip = true; } catch { clip = false; }
    }
    for (let i = 0; i < pasos; i++) {
      const [rr, gg, bb] = mezcla(c1, c2, i / (pasos - 1));
      doc.setFillColor(rr, gg, bb);
      if (vertical) doc.rect(x, y + (h * i) / pasos, w, h / pasos + 0.3, 'F');
      else doc.rect(x + (w * i) / pasos, y, w / pasos + 0.3, h, 'F');
    }
    if (clip) doc.restoreGraphicsState();
    else if (r > 0) {
      // sin clip: no se pueden redondear las esquinas; se dibuja un borde suave del mismo color
      doc.setDrawColor(c1); doc.setLineWidth(0.2); doc.roundedRect(x, y, w, h, r, r, 'S');
    }
  }
  // Formas circulares sutiles de fondo (banda de portada / diploma)
  function burbujas(x, y, w, h) {
    opacidad(() => {
      doc.setFillColor(C.blanco);
      doc.circle(x + w * 0.78, y + h * 0.1, h * 0.75, 'F');
      doc.circle(x + w * 0.62, y + h * 1.05, h * 0.55, 'F');
      doc.circle(x + w * 0.95, y + h * 0.9, h * 0.35, 'F');
    }, 0.07);
  }
  // Chip redondeado con texto
  function chip(txt, x, y, { fill = C.blue100, color = C.blue700, size = 6.4, bold = true, padX = 2.2, h = 4.6, align = 'left' } = {}) {
    F(bold ? 'bold' : 'normal', size, color);
    const w = doc.getTextWidth(txt) + padX * 2;
    const xx = align === 'right' ? x - w : x;
    doc.setFillColor(fill); doc.roundedRect(xx, y, w, h, h / 2, h / 2, 'F');
    doc.setTextColor(color); doc.text(txt, xx + padX, y + h / 2 + size * 0.125, { baseline: 'middle' });
    return w;
  }
  function etiqueta(txt, x, y, color = C.blue700, size = 6.8, align = 'left') {
    F('bold', size, color); doc.text(txt, x, y, { charSpace: 0.6, align });
  }
  function textoWrap(txt, x, y, w, size = 8.4, color = C.gray700, estilo = 'normal', lh = 1.45) {
    F(estilo, size, color);
    const lines = doc.splitTextToSize(String(txt || ''), w);
    doc.text(lines, x, y);
    return y + lines.length * size * 0.3528 * lh;
  }
  function alturaTexto(txt, w, size = 8.4, lh = 1.45) {
    F('normal', size);
    return doc.splitTextToSize(String(txt || ''), w).length * size * 0.3528 * lh;
  }
  function marcaAgua() {
    if (!isotipo) return;
    const s = 110;
    opacidad(() => { try { doc.addImage(isotipo.dataUrl, 'PNG', (W - s) / 2, (H - s) / 2 - 4, s, s, 'beep-isotipo', 'FAST'); } catch { /* sin marca */ } }, 0.06);
  }

  // ---- tipos de pagina (pie) ----
  const tiposPagina = [];
  const marcarPagina = (t) => { tiposPagina[doc.internal.getCurrentPageInfo().pageNumber - 1] = t; };

  // ---- Banda delgada (paginas siguientes / secciones) ----
  function bandaDelgada(titulo) {
    degradado(0, 0, W, 12, C.navy, C.blue);
    if (logoBlanco) { const h2 = 6.2, w2 = h2 * (logoBlanco.w / logoBlanco.h); try { doc.addImage(logoBlanco.dataUrl, 'PNG', M, 2.9, w2, h2, 'beep-logo-blanco', 'FAST'); } catch { /* */ } }
    else { F('black', 11, C.blanco); doc.text('beep', M, 8.4); }
    F('bold', 7.2, C.blanco); doc.text(titulo, W - M, 6.2, { align: 'right', charSpace: 0.5 });
    F('normal', 6.6, '#CFE2F5'); doc.text(`Folio ${cot.folio || '—'}`, W - M, 9.8, { align: 'right' });
  }
  const headerCot = () => { bandaDelgada('PROPUESTA COMERCIAL'); marcarPagina('cot'); };
  const headerPago = () => { bandaDelgada('CONDICIONES Y DATOS DE PAGO'); marcarPagina('pago'); };
  const headerCert = () => { bandaDelgada('CERTIFICADO DE GARANTÍA'); marcarPagina('cert'); };

  // ---- Pie ----
  function dibujarFooter(pagina, total, tipo) {
    doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.line(M, H - 12, W - M, H - 12);
    F('bold', 7.2, C.gray400); doc.text('Beep México', M, H - 7.2);
    F('normal', 7.2, C.gray400); doc.text('  ·  Soluciones tecnológicas integrales', M + doc.getTextWidth('Beep México') + 0.5, H - 7.2);
    doc.text(`Página ${pagina} de ${total}`, W - M, H - 7.2, { align: 'right' });
    if (tipo === 'cert' && cert && cert.codigo) {
      F('normal', 4, C.blue300);
      const micro = `CERTIFICADO ${cert.folio} · CÓDIGO ${cert.codigo} · Beep México · documento oficial · verifique en línea · `;
      doc.text(micro.repeat(3).slice(0, 250), M, H - 3.6);
    } else {
      F('normal', 6.2, C.gray400); doc.text([emisor.web, emisor.correo, emisor.telefono].filter(Boolean).join('  ·  '), M, H - 3.6);
    }
  }

  // ---- Tabla de lineas COMPARTIDA (cotizacion y certificado) ----
  const esServ = (l) => !!l && (l.tipo === 'servicio');

  // ---- Bloque de texto de la columna "Producto" ----
  // DEFECTO-PDF-1 (Ricardo, 2026-09-11): el nombre y la descripcion salian CORTADOS a media frase.
  // Eran dos causas sumadas: (a) el texto se recortaba a 2 lineas con `.slice(0, 2)` antes de
  // dibujarlo, y (b) la celda de esa columna se alimentaba con cadena VACIA, asi que autoTable no
  // tenia nada que medir y la fila se quedaba en el `minCellHeight` fijo de 19 mm.
  // Ahora esta funcion es la UNICA fuente: mide el bloque completo (sin recortes) y el mismo
  // resultado se usa para fijar el alto de la fila y para dibujar. Asi no se pueden desincronizar.
  const PASO_NOMBRE = 3.5, PASO_DETALLE = 2.9, ALTO_CHIP = 5.2, AIRE_CELDA = 2.6;
  // PDF-DESCRIPCION: letra mas chica y gris que el resto, y como mucho 4 renglones por producto para que una ficha larga
  // de Syscom no estire la tabla (el texto ya llega limpio del servidor; aqui se repasa por si acaso).
  const PASO_DESC = 2.5, MAX_RENGLONES_DESC = 4;
  function bloqueProducto(l, wTxt, conDesc = false) {
    const { corto, detalle } = partirNombre(l && l.nombre);
    F('bold', 8);
    const lc = doc.splitTextToSize(corto, wTxt);            // nombre COMPLETO
    F('normal', 6.4);
    const ld = detalle ? doc.splitTextToSize(detalle, wTxt) : []; // descripcion COMPLETA
    const marcaTxt = l && l.marca && !/syscom/i.test(String(l.marca)) ? String(l.marca).toUpperCase() : '';
    const modeloTxt = l && l.modelo ? String(l.modelo) : '';
    const tieneLinea2 = esServ(l) || !!marcaTxt || !!modeloTxt;
    let lx = [];
    const desc = conDesc ? textoDescripcion(l && l.descripcion) : '';
    if (desc) {
      F('normal', 5.8);
      lx = doc.splitTextToSize(desc, wTxt);
      if (lx.length > MAX_RENGLONES_DESC) {
        lx = lx.slice(0, MAX_RENGLONES_DESC);
        let ult = lx[MAX_RENGLONES_DESC - 1].replace(/[\s.,;:]+$/, '');
        while (ult && doc.getTextWidth(ult + '...') > wTxt) ult = ult.replace(/\s*\S+$/, '');
        lx[MAX_RENGLONES_DESC - 1] = ult + '...';
      }
    }
    const hBloque = lc.length * PASO_NOMBRE + (tieneLinea2 ? ALTO_CHIP : 0) + ld.length * PASO_DETALLE
      + (lx.length ? 0.8 + lx.length * PASO_DESC : 0);
    return { lc, ld, lx, marcaTxt, modeloTxt, tieneLinea2, hBloque };
  }

  function tablaLineas(startY, modo, headerFn) {
    const esGar = modo === 'garantia';
    const conDesc = mostrarDescripcion && !esGar;
    const head = esGar ? [['', 'Cant.', 'Producto / servicio', 'Garantía', 'Vence']] : [['', 'Cant.', 'Producto', 'Unitario', 'Importe']];
    const body = lineas.map((l, i) => {
      const cols = ['', String(Math.max(1, Math.round(Number(l.cantidad) || 1))), ''];
      if (esGar) {
        cols.push(''); // chip dibujado a mano
        const venc = cert && cert.lineas && cert.lineas[i] && cert.lineas[i].fecha_vencimiento;
        cols.push(venc ? fmtFecha(venc) : 'desde la entrega');
      } else {
        cols.push(fmtMonto(Number(l.precio_unitario) || 0));
        cols.push(fmtMonto(Number(l.subtotal) || 0));
      }
      return cols;
    });
    // La columna 2 ("Producto") es la unica flexible: su ancho es lo que sobra de las fijas.
    // Se calcula aqui para poder MEDIR el texto antes de que autoTable arme la tabla.
    const COL_PRODUCTO = 2;
    const anchoProducto = CW - 20 - 12 - (esGar ? 34 : 28) - 30;
    autoTable(doc, {
      startY, head, body, theme: 'plain',
      styles: { font: fam, overflow: 'linebreak' },
      headStyles: { fillColor: false, textColor: C.blanco, fontSize: 7.4, fontStyle: 'bold', halign: 'center', valign: 'middle', minCellHeight: 9, cellPadding: { top: 2, bottom: 2, left: 2, right: 2 } },
      bodyStyles: { fontSize: 8, textColor: C.gray700, valign: 'middle', minCellHeight: 19, cellPadding: { top: 2.2, bottom: 2.2, left: 2, right: 2 } },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20 },
        1: { halign: 'center', cellWidth: 12, fontStyle: 'bold' },
        2: { halign: 'left' },
        3: { halign: esGar ? 'center' : 'right', cellWidth: esGar ? 34 : 28, textColor: C.gray500 },
        4: { halign: esGar ? 'center' : 'right', cellWidth: 30, fontStyle: 'bold', textColor: C.ink },
      },
      margin: { left: M, right: M, top: 18, bottom: 18 },
      rowPageBreak: 'avoid', // FIX 20: una fila jamas se parte (evita la fila fantasma al inicio de la continuacion)
      showHead: 'everyPage', // si la tabla sigue en la pagina siguiente, repite el encabezado
      didDrawPage: (data) => { if (data.pageNumber > 1 && headerFn) headerFn(); },
      // DEFECTO-PDF-1: la columna "Producto" se dibuja a mano (chips, tamanos y colores propios),
      // asi que su celda va vacia y autoTable no puede medirla: la fila se quedaba en los 19 mm de
      // `minCellHeight` y el texto sobrante se perdia. Aqui se le dice el alto que REALMENTE necesita
      // ese texto, y la fila crece sola. El tope es el alto util de una pagina, porque con
      // `rowPageBreak: 'avoid'` una fila no puede partirse entre dos hojas.
      didParseCell: (data) => {
        if (data.section !== 'body' || data.column.index !== COL_PRODUCTO) return;
        const l = lineas[data.row.index];
        if (!l) return;
        const { hBloque } = bloqueProducto(l, anchoProducto - 4, conDesc);
        const necesario = hBloque + AIRE_CELDA * 2 + 2.2;
        data.cell.styles.minCellHeight = Math.min(Math.max(19, necesario), H - 18 - 18 - 12);
      },
      willDrawCell: (data) => {
        // Encabezado: una sola pieza azul con esquinas superiores redondeadas
        if (data.section === 'head' && data.column.index === 0) {
          const x = data.cell.x, y = data.cell.y, h = data.row.height;
          doc.setFillColor(C.navy);
          doc.roundedRect(x, y, CW, h, 2.2, 2.2, 'F');
          doc.rect(x, y + h / 2, CW, h / 2, 'F');
        }
        if (data.section === 'body') {
          const l = lineas[data.row.index];
          const alt = data.row.index % 2 === 1;
          const fill = (esGar || true) && esServ(l) ? C.verdeSuave : (alt ? C.gray50 : C.blanco);
          if (data.column.index === 0) {
            doc.setFillColor(fill); doc.rect(data.cell.x, data.cell.y, CW, data.row.height, 'F');
            doc.setDrawColor(C.gray200); doc.setLineWidth(0.25); doc.line(data.cell.x, data.cell.y + data.row.height, data.cell.x + CW, data.cell.y + data.row.height);
          }
          data.cell.styles.fillColor = false;
        }
      },
      didDrawCell: (data) => {
        if (data.section !== 'body') return;
        const l = lineas[data.row.index] || {};
        if (data.column.index === 2) {
          const x = data.cell.x + 2, wTxt = data.cell.width - 4;
          // Mismo calculo que fijo el alto de la fila (ver bloqueProducto): nada se recorta.
          const { lc, ld, lx, marcaTxt, modeloTxt, hBloque } = bloqueProducto(l, wTxt, conDesc);
          let yy = data.cell.y + Math.max(AIRE_CELDA, (data.cell.height - hBloque) / 2) + AIRE_CELDA;
          F('bold', 8, C.ink); doc.text(lc, x, yy); yy += lc.length * PASO_NOMBRE;
          if (esServ(l)) {
            chip('SERVICIO BEEP', x, yy - 0.4, { fill: C.verde100, color: C.verde700, size: 5.6, h: 4 });
            yy += ALTO_CHIP;
          } else if (marcaTxt || modeloTxt) {
            let cx = x;
            if (marcaTxt) { cx += chip(marcaTxt, x, yy - 0.4, { size: 5.6, h: 4 }) + 1.6; }
            if (modeloTxt) { F('bold', 6.8, C.blue); doc.text(doc.splitTextToSize(modeloTxt, wTxt - (cx - x))[0], cx, yy + 2.4); }
            yy += ALTO_CHIP;
          }
          if (ld.length) { F('normal', 6.4, C.gray500); doc.text(ld, x, yy + 0.4); yy += ld.length * PASO_DETALLE; }
          if (lx.length) { F('normal', 5.8, C.gray400); doc.text(lx, x, yy + 0.8 + 0.4, { lineHeightFactor: PASO_DESC / (5.8 * 0.3528) }); }
          return;
        }
        if (esGar && data.column.index === 3) {
          const g = fmtGarantia(l.garantia_meses, l.garantia_dias);
          const beep = esServ(l) || l.garantia_origen === 'mano de obra';
          const w = (() => { F('bold', 6.6); return doc.getTextWidth(g) + 5; })();
          const cx = data.cell.x + (data.cell.width - w) / 2, cy = data.cell.y + data.cell.height / 2 - 4.2;
          chip(g, cx, cy, { fill: beep ? C.verde100 : C.blue100, color: beep ? C.verde700 : C.blue700, size: 6.6, h: 5, padX: 2.5 });
          F('normal', 5.6, C.gray400); doc.text(beep ? 'Beep México' : 'Fabricante', data.cell.x + data.cell.width / 2, cy + 8.2, { align: 'center' });
          return;
        }
        if (data.column.index !== 0) return;
        // Miniatura con fondo suave
        const m = 2.2, bx = data.cell.x + m, by = data.cell.y + m, bw = data.cell.width - m * 2, bh = data.cell.height - m * 2;
        if (bw <= 2 || bh <= 2) return;
        doc.setFillColor(C.gray100); doc.roundedRect(bx, by, bw, bh, 1.8, 1.8, 'F');
        const info = imgs[data.row.index];
        if (info) {
          try {
            const maxW = bw - 2.4, maxH = bh - 2.4;
            const ratio = Math.min(maxW / info.w, maxH / info.h);
            const w = info.w * ratio, h = info.h * ratio;
            doc.addImage(info.dataUrl, info.fmt, bx + (bw - w) / 2, by + (bh - h) / 2, w, h, `linea-${data.row.index}`, 'FAST');
          } catch { /* imagen invalida */ }
          return;
        }
        const modeloTxt = l.modelo || '';
        if (modeloTxt) {
          F('bold', 5.8, C.gray500);
          const partes = doc.splitTextToSize(String(modeloTxt).slice(0, 24), bw - 2).slice(0, 2);
          let ty = by + (bh - partes.length * 2.6) / 2 + 2;
          for (const p of partes) { doc.text(p, bx + bw / 2, ty, { align: 'center' }); ty += 2.6; }
        } else {
          dibujarIcono(doc, esServ(l) ? 'wrench' : 'package', bx + (bw - 7) / 2, by + (bh - 7) / 2, 7, C.gray400);
        }
      },
    });
    return doc.lastAutoTable ? doc.lastAutoTable.finalY : startY;
  }

  function tituloSeccion(txt, y, color = C.navy) {
    etiqueta(txt, M, y, color, 7.2);
    doc.setDrawColor(C.blue300); doc.setLineWidth(0.4); doc.line(M, y + 2.4, M + 14, y + 2.4);
    return y + 7.5;
  }
  const necesita = (y, h, headerFn, yIni = 20) => { if (y + h > H - 16) { doc.addPage(); headerFn(); return yIni; } return y; };

  // ============================================
  // SECCION 1 — COTIZACION
  // ============================================
  marcarPagina('cot');
  const bandaH = 58;
  degradado(0, 0, W, bandaH, C.navy, C.blue);
  burbujas(0, 0, W, bandaH);
  // Emisor (izquierda, blanco)
  let ey = 13;
  if (logoBlanco) { const hL = 13, wL = hL * (logoBlanco.w / logoBlanco.h); try { doc.addImage(logoBlanco.dataUrl, 'PNG', M, ey, wL, hL, 'beep-logo-blanco', 'FAST'); } catch { /* */ } ey += hL + 5; }
  else { F('black', 22, C.blanco); doc.text('beep', M, ey + 8); ey += 14; }
  F('bold', 8.2, C.blanco); doc.text(String(emisor.razonSocial || ''), M, ey); ey += 4.2;
  F('normal', 6.8, '#DCEAF8');
  if (emisor.rfc) { doc.text(`RFC ${emisor.rfc}`, M, ey); ey += 3.6; }
  if (emisor.direccion) { const dl = doc.splitTextToSize(emisor.direccion, 96).slice(0, 2); doc.text(dl, M, ey); ey += dl.length * 3.4; }
  const contactoEmisor = [emisor.telefono ? `Tel. ${emisor.telefono}` : '', emisor.correo, emisor.web].filter(Boolean).join('   ·   ');
  if (contactoEmisor) { doc.text(contactoEmisor, M, ey); ey += 3.6; }
  // Derecha: PROPUESTA COMERCIAL + FOLIO + chip vigencia
  F('bold', 7.4, '#CFE2F5'); doc.text('PROPUESTA COMERCIAL', W - M, 17, { align: 'right', charSpace: 1.2 });
  F('black', 26, C.blanco); doc.text(String(cot.folio || '—'), W - M, 29, { align: 'right' });
  const vigTxt = `Vigente hasta ${fmtFechaLarga(cot.vigencia)}`;
  F('bold', 6.8); const vw = doc.getTextWidth(vigTxt) + 7;
  opacidad(() => { doc.setFillColor(C.blanco); doc.roundedRect(W - M - vw, 34, vw, 6.4, 3.2, 3.2, 'F'); }, 0.18);
  F('bold', 6.8, C.blanco); doc.text(vigTxt, W - M - vw / 2, 37.2, { align: 'center', baseline: 'middle' });
  F('normal', 6.6, '#DCEAF8'); doc.text(`Emitida el ${fmtFecha(cot.fecha)}${cot.expedienteFolio ? '   ·   Expediente ' + cot.expedienteFolio : ''}`, W - M, 46, { align: 'right' });

  let y = bandaH + 12;
  // PROYECTO + titulo
  etiqueta('PROYECTO', M, y, C.blue); y += 6;
  F('black', 15, C.ink);
  const titulo = doc.splitTextToSize(String(cot.nombre || 'Propuesta comercial'), CW).slice(0, 2);
  doc.text(titulo, M, y); y += titulo.length * 6.4 + 4;

  // 3 tarjetas: Cliente / Atiende / Fecha
  const gap = 4, cardW = (CW - gap * 2) / 3, cardH = 19;
  const tarjetas = [
    // El nombre del cliente es dato REAL o "Sin cliente registrado": nunca una etiqueta de relleno
    // (defecto en OP-2026-0338, que imprimia la palabra "Expediente" como si fuera el cliente).
    // El folio del expediente es SECUNDARIO y se suma a la sucursal cuando ambos existen.
    ['CLIENTE', cliente.nombre || 'Sin cliente registrado',
      [cliente.sucursal ? `Sucursal ${cliente.sucursal}` : '', cot.expedienteFolio ? `Expediente ${cot.expedienteFolio}` : '']
        .filter(Boolean).join(' · '), 'user'],
    ['ATIENDE', cliente.vendedor || 'Beep México', cliente.vendedorCorreo || emisor.correo || '', 'mail'],
    ['FECHA', fmtFecha(cot.fecha), `Vigencia ${fmtFecha(cot.vigencia)}`, 'calendar'],
  ];
  tarjetas.forEach(([lab, val, sub, ico], i) => {
    const x = M + i * (cardW + gap);
    doc.setFillColor(C.gray50); doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.roundedRect(x, y, cardW, cardH, 2.4, 2.4, 'FD');
    doc.setFillColor(C.blue100); doc.roundedRect(x + 3, y + 4.2, 10, 10, 2, 2, 'F');
    dibujarIcono(doc, ico, x + 4.8, y + 6, 6.4, C.blue700);
    etiqueta(lab, x + 16, y + 6.2, C.gray400, 5.8);
    F('bold', 8.2, C.ink); doc.text(doc.splitTextToSize(String(val), cardW - 19)[0], x + 16, y + 11);
    if (sub) { F('normal', 6.4, C.gray500); doc.text(doc.splitTextToSize(String(sub), cardW - 19)[0], x + 16, y + 15.4); }
  });
  y += cardH + 8;

  // TABLA
  let ty = tablaLineas(y, 'precios', headerCot) + 6;

  // ---- PAGINA / BLOQUE DE TOTALES ----
  const nProductos = lineas.filter(l => !esServ(l)).reduce((a, l) => a + Math.max(1, Math.round(Number(l.cantidad) || 1)), 0);
  const nServicios = lineas.filter(esServ).length;
  const notaTxt = cot.nota && String(cot.nota).trim() ? String(cot.nota).trim() : '';
  const colIzqW = CW * 0.56, colDerW = CW - colIzqW - 6, xDer = M + colIzqW + 6;
  const hNotas = notaTxt ? 12 + alturaTexto(notaTxt, colIzqW - 8, 7.6) : 0;
  const bloqueH = 8 + 20 + 6 + hNotas + (hNotas ? 6 : 0) + 34 + 8;
  if (ty + Math.max(bloqueH, 70) > H - 30) { doc.addPage(); headerCot(); ty = 22; }
  let yb = ty;
  // Izquierda: RESUMEN
  yb = tituloSeccion('RESUMEN DE LA PROPUESTA', yb + 4);
  const miniW = (colIzqW - 8) / 3;
  [['Partidas', String(lineas.length)], ['Equipos', String(nProductos)], ['Servicios', String(nServicios)]].forEach(([lab, val], i) => {
    const x = M + i * (miniW + 4);
    doc.setFillColor(C.blanco); doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.roundedRect(x, yb, miniW, 17, 2.2, 2.2, 'FD');
    F('black', 14, C.navy); doc.text(val, x + 4, yb + 9.4);
    F('normal', 6.4, C.gray500); doc.text(lab, x + 4, yb + 14);
  });
  yb += 17 + 6;
  if (notaTxt) {
    doc.setFillColor(C.ambarSuave); doc.setDrawColor(C.ambarBorde); doc.setLineWidth(0.3);
    doc.roundedRect(M, yb, colIzqW, hNotas, 2.2, 2.2, 'FD');
    etiqueta('NOTAS DE LA PROPUESTA', M + 4, yb + 5.4, C.ambar700, 6.4);
    textoWrap(notaTxt, M + 4, yb + 10.2, colIzqW - 8, 7.6, '#6B4A0A');
    yb += hNotas + 6;
  }
  // Incluye / No incluye
  const incluye = ['Equipos y materiales listados en esta propuesta'];
  if (nServicios) incluye.push('Instalación, configuración y puesta en marcha por Beep México');
  if (incluirCert) incluye.push('Garantía de fabricante en equipos y garantía Beep en la instalación');
  const noIncluye = ['Obra civil, permisos o trabajos no descritos en las partidas', 'Suministro eléctrico, internet o infraestructura del cliente', 'Partidas o servicios no listados'];
  const colInc = (colIzqW - 4) / 2;
  const hInc = 8 + Math.max(incluye.length, noIncluye.length) * 7.4;
  const dibujarLista = (x, yTop, tit, items, ok) => {
    etiqueta(tit, x, yTop, ok ? C.verde700 : C.gray500, 6.4);
    let yy = yTop + 5;
    for (const it of items) {
      doc.setFillColor(ok ? C.verde100 : C.gray100); doc.circle(x + 2, yy + 1.2, 2, 'F');
      dibujarIcono(doc, ok ? 'check' : 'x', x + 0.5, yy - 0.3, 3, ok ? C.verde700 : C.gray500);
      F('normal', 6.6, C.gray700); const li = doc.splitTextToSize(it, colInc - 8).slice(0, 2); doc.text(li, x + 6, yy + 2.2); yy += Math.max(7.4, li.length * 3.2 + 2);
    }
  };
  dibujarLista(M, yb + 2, 'INCLUYE', incluye, true);
  dibujarLista(M + colInc + 4, yb + 2, 'NO INCLUYE', noIncluye, false);
  const yFinIzq = yb + hInc + 4;

  // Derecha: INVERSION TOTAL
  const letra = importeALetras(totales.total, moneda);
  F('normal', 6.6); const letraLines = doc.splitTextToSize(`(${letra})`, colDerW - 10);
  const invH = 56 + letraLines.length * 3.2;
  const yInv = ty + 4;
  // sombra suave
  opacidad(() => { doc.setFillColor(C.navy); doc.roundedRect(xDer + 1.2, yInv + 1.8, colDerW, invH, 3.5, 3.5, 'F'); }, 0.12);
  degradado(xDer, yInv, colDerW, invH, C.navy, C.blue, 3.5, true);
  etiqueta('INVERSIÓN TOTAL', xDer + 6, yInv + 8, '#CFE2F5', 6.8);
  F('normal', 7.6, '#DCEAF8'); doc.text('Subtotal', xDer + 6, yInv + 17); doc.text(fmtMonto(totales.subtotal), xDer + colDerW - 6, yInv + 17, { align: 'right' });
  doc.text(`IVA ${Math.round(TASA_IVA * 100)}%`, xDer + 6, yInv + 23); doc.text(fmtMonto(totales.iva), xDer + colDerW - 6, yInv + 23, { align: 'right' });
  opacidad(() => { doc.setDrawColor(C.blanco); doc.setLineWidth(0.3); doc.line(xDer + 6, yInv + 26.5, xDer + colDerW - 6, yInv + 26.5); }, 0.35);
  F('bold', 7.4, '#CFE2F5'); doc.text('TOTAL', xDer + 6, yInv + 33.5);
  F('black', 17, C.blanco); doc.text(fmtMonto(totales.total), xDer + colDerW - 6, yInv + 38, { align: 'right' });
  F('normal', 6.6, '#DCEAF8'); doc.text(letraLines, xDer + 6, yInv + 44);
  const yChip = yInv + 44 + letraLines.length * 3.2 + 1;
  opacidad(() => { doc.setFillColor(C.blanco); doc.roundedRect(xDer + 6, yChip, 34, 5.4, 2.7, 2.7, 'F'); }, 0.18);
  F('bold', 6.2, C.blanco); doc.text(`${moneda}  ·  IVA incluido`, xDer + 6 + 17, yChip + 2.7, { align: 'center', baseline: 'middle' });
  F('normal', 6.2, '#DCEAF8'); doc.text(`Vigente hasta ${fmtFecha(cot.vigencia)}`, xDer + colDerW - 6, yChip + 3.6, { align: 'right' });
  let yFin = Math.max(yFinIzq, yInv + invH + 6);

  // Pie de la seccion: asesor + leyenda (FIX 19: aqui vive el texto que antes hacia una pagina casi vacia)
  yFin = necesita(yFin, 20, headerCot);
  doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.line(M, yFin, W - M, yFin);
  F('bold', 7.4, C.ink); doc.text(cliente.vendedor ? `Tu asesor: ${cliente.vendedor}` : 'Beep México', M, yFin + 5.6);
  F('normal', 6.6, C.gray500);
  const contactoAsesor = [cliente.vendedorCorreo, emisor.telefono ? `Tel. ${emisor.telefono}` : '', emisor.correo && emisor.correo !== cliente.vendedorCorreo ? emisor.correo : ''].filter(Boolean).join('   ·   ');
  if (contactoAsesor) doc.text(contactoAsesor, M, yFin + 9.6);
  F('normal', 6.6, C.gray500);
  const leyenda = incluirPago
    ? `Precios en ${moneda}. Condiciones de pago y datos bancarios en la sección siguiente. Gracias por su preferencia.`
    : `Precios en ${moneda}. Vigencia según la fecha indicada. Gracias por su preferencia.`;
  const leyendaLineas = doc.splitTextToSize(leyenda, CW);
  doc.text(leyendaLineas, M, yFin + (contactoAsesor ? 14 : 10));

  // ---- TERMINOS Y CONDICIONES (TERMINOS-COTIZACION, 2026-10-05) ----
  // Al final de la cotizacion, despues de los importes. El texto llega YA RESUELTO desde la foto
  // guardada en la cotizacion (`cotizaciones.terminos`): aqui no se reescribe ni se resume nada.
  // Se dibuja LINEA A LINEA y cada linea comprueba si cabe: si no, pagina nueva con el encabezado
  // de la cotizacion y el titulo de la seccion repetidos. Asi ninguna clausula se recorta, por
  // larga que sea (la leccion del DEFECTO-PDF-1, donde las descripciones se cortaban).
  // Sin terminos (cotizacion anterior a la funcion) este bloque no existe y el PDF sale igual.
  const terminosCot = (Array.isArray(cot.terminos) ? cot.terminos : [])
    .filter(t => t && String(t.texto || '').trim());
  if (terminosCot.length) {
    const T_TIT = 7.6, T_TXT = 7.2, LH = 1.42;
    const pasoTit = T_TIT * 0.3528 * LH, pasoTxt = T_TXT * 0.3528 * LH;
    const sangria = 7, anchoTxt = CW - sangria;
    const limite = H - 18; // por encima del pie
    const tituloTerminos = (yy, cont) => {
      F('black', 11, C.ink); doc.text(cont ? 'Términos y condiciones (continuación)' : 'Términos y condiciones', M, yy);
      doc.setDrawColor(C.blue300); doc.setLineWidth(0.4); doc.line(M, yy + 2.2, M + 14, yy + 2.2);
      return yy + 8;
    };
    const nuevaPagina = () => { doc.addPage(); headerCot(); return tituloTerminos(24, true); };
    let yt = yFin + (contactoAsesor ? 14 : 10) + leyendaLineas.length * 3 + 9;
    // El titulo nunca se queda solo al pie: con el y la primera linea de la primera clausula.
    if (yt + 8 + pasoTit + pasoTxt > limite) { doc.addPage(); headerCot(); yt = 24; }
    yt = tituloTerminos(yt, false);
    terminosCot.forEach((t, i) => {
      F('bold', T_TIT); const lt = doc.splitTextToSize(String(t.titulo || '').trim(), anchoTxt);
      F('normal', T_TXT); const lx = doc.splitTextToSize(String(t.texto).trim(), anchoTxt);
      // titulo + al menos una linea de texto juntos (no dejar un titulo huerfano al pie)
      if (yt + lt.length * pasoTit + pasoTxt > limite) yt = nuevaPagina();
      F('bold', T_TIT, C.navy); doc.text(`${i + 1}.`, M, yt);
      lt.forEach(l => { F('bold', T_TIT, C.ink); doc.text(l, M + sangria, yt); yt += pasoTit; });
      lx.forEach(l => {
        if (yt + pasoTxt > limite) yt = nuevaPagina();
        F('normal', T_TXT, C.gray700); doc.text(l, M + sangria, yt); yt += pasoTxt;
      });
      yt += 2.2;
    });
  }

  // ============================================
  // SECCION 2 — CONDICIONES Y DATOS DE PAGO
  // ============================================
  if (incluirPago) {
    doc.addPage(); headerPago();
    let py = 24;
    F('black', 15, C.ink); doc.text('Condiciones y datos de pago', M, py); py += 4;
    F('normal', 7.4, C.gray500); doc.text(`Propuesta ${cot.folio || '—'}${cot.nombre ? ' · ' + doc.splitTextToSize(String(cot.nombre), CW - 40)[0] : ''}`, M, py + 4); py += 12;

    const ant = Number.isFinite(Number(cot.anticipoPct)) ? Math.min(100, Math.max(0, Math.round(Number(cot.anticipoPct)))) : 100;
    const tarjetasPago = [
      // TERMINOS-COTIZACION: el saldo puede tener plazo (cot.saldoDias). Sin plazo, el texto de siempre.
      ['FORMA DE PAGO', `${ant}%`, ant >= 100 ? 'a la confirmación del pedido'
        : (Number(cot.saldoDias) >= 1 ? `de anticipo · ${100 - ant}% a ${Number(cot.saldoDias)} días de la entrega` : `de anticipo · ${100 - ant}% contra entrega`), 'credit-card'],
      ['ENTREGA', cot.tiempoEntrega ? String(cot.tiempoEntrega) : 'A convenir', cot.tiempoEntrega ? 'a partir de la confirmación del anticipo' : 'tras la confirmación del anticipo', 'truck'],
      ['VIGENCIA', fmtFecha(cot.vigencia), 'después, precios y existencias pueden cambiar', 'calendar'],
    ];
    const pcW = (CW - gap * 2) / 3, pcH = 30;
    tarjetasPago.forEach(([lab, val, sub, ico], i) => {
      const x = M + i * (pcW + gap);
      doc.setFillColor(C.blanco); doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.roundedRect(x, py, pcW, pcH, 2.6, 2.6, 'FD');
      doc.setFillColor(C.blue100); doc.roundedRect(x + 4, py + 4.5, 11, 11, 2.4, 2.4, 'F');
      dibujarIcono(doc, ico, x + 6, py + 6.5, 7, C.blue700);
      etiqueta(lab, x + 18.5, py + 8.4, C.gray400, 5.8);
      const grande = String(val);
      F('black', grande.length > 10 ? 10 : 15, C.navy);
      doc.text(doc.splitTextToSize(grande, pcW - 21)[0], x + 18.5, py + 15.6);
      F('normal', 6.2, C.gray500); doc.text(doc.splitTextToSize(sub, pcW - 8).slice(0, 2), x + 4, py + 22);
    });
    py += pcH + 10;

    py = tituloSeccion('DATOS PARA TRANSFERENCIA', py);
    const correoPagos = gcfg.correo_comprobantes || emisor.correo || '';
    if (!cuentas.length) {
      doc.setFillColor(C.gray50); doc.setDrawColor(C.gray200); doc.roundedRect(M, py, CW, 14, 2.4, 2.4, 'FD');
      F('normal', 7.8, C.gray500); doc.text('Los datos bancarios se proporcionan al confirmar el pedido.', M + 5, py + 8.4); py += 20;
    } else {
      for (const c of cuentas) {
        const bH = 46;
        py = necesita(py, bH + 4, headerPago, 22);
        opacidad(() => { doc.setFillColor(C.navy); doc.roundedRect(M + 1, py + 1.6, CW, bH, 3.5, 3.5, 'F'); }, 0.12);
        degradado(M, py, CW, bH, C.cardDark1, C.cardDark2, 3.5);
        // burbuja decorativa
        opacidad(() => { doc.setFillColor(C.blanco); doc.circle(M + CW - 12, py + 6, 22, 'F'); }, 0.05);
        etiqueta('BENEFICIARIO', M + 7, py + 8, '#9EC2E5', 5.8);
        F('bold', 9, C.blanco); doc.text(doc.splitTextToSize(String(c.beneficiario || emisor.razonSocial || ''), CW - 60)[0], M + 7, py + 13.6);
        chip(String(c.moneda || 'MXN'), M + CW - 7, py + 6, { fill: '#2E7AC4', color: C.blanco, size: 6.4, h: 5.2, align: 'right' });
        F('bold', 7.6, C.blanco); doc.text(String(c.banco || ''), M + CW - 7, py + 16.4, { align: 'right' });
        if (c.sucursal) { F('normal', 6, '#9EC2E5'); doc.text(`Sucursal ${c.sucursal}`, M + CW - 7, py + 20.2, { align: 'right' }); }
        const colBW = (CW - 14) / 2;
        etiqueta('NO. DE CUENTA', M + 7, py + 24.6, '#9EC2E5', 5.6);
        MONO(true, 11, C.blanco); doc.text(String(c.numero_cuenta || '—'), M + 7, py + 30.6);
        etiqueta('CLABE INTERBANCARIA', M + 7 + colBW, py + 24.6, '#9EC2E5', 5.6);
        MONO(true, 11, C.blanco); doc.text(String(c.clabe || (c.swift ? `SWIFT ${c.swift}` : '—')), M + 7 + colBW, py + 30.6);
        opacidad(() => { doc.setDrawColor(C.blanco); doc.setLineWidth(0.25); doc.line(M + 7, py + 35, M + CW - 7, py + 35); }, 0.3);
        F('normal', 6.6, '#DCEAF8');
        const instr = correoPagos ? `Envía tu comprobante a ${correoPagos} indicando el folio ${cot.folio || ''}.` : `Envía tu comprobante de pago indicando el folio ${cot.folio || ''}.`;
        doc.text(doc.splitTextToSize(instr, CW - 14)[0], M + 7, py + 40.6);
        py += bH + 6;
      }
    }

    // TERMINOS COMERCIALES en dos columnas (check / equis). TERMINOS-COTIZACION: si la cotizacion
    // ya lleva sus "Terminos y condiciones" (al final de la seccion 1), este bloque generico NO se
    // imprime: dos juegos de condiciones en el mismo documento podrian contradecirse. Las
    // cotizaciones anteriores (sin terminos) lo siguen imprimiendo exactamente igual.
    if (!terminosCot.length) {
    const terminosTxt = gcfg.terminos_comerciales || 'Precios en pesos mexicanos. La cotización tiene la vigencia indicada; pasada esa fecha los precios y existencias pueden cambiar.';
    const frases = String(terminosTxt).split(/(?<=\.)\s+|\n+/).map(s => s.trim()).filter(Boolean);
    const aplica = frases.filter(f => !/\bno (incluye|se incluye|cubre|aplica|contempla)\b/i.test(f));
    const noAplica = frases.filter(f => /\bno (incluye|se incluye|cubre|aplica|contempla)\b/i.test(f));
    if (!noAplica.length) noAplica.push(...noIncluye);
    const colT = (CW - 6) / 2;
    const altLista = (items) => items.reduce((a, it) => a + Math.max(6.6, alturaTexto(it, colT - 9, 7) + 2.2), 0);
    const hTerm = 8 + Math.max(altLista(aplica), altLista(noAplica));
    py = necesita(py, hTerm + 12, headerPago, 22);
    py = tituloSeccion('TÉRMINOS COMERCIALES', py + 2);
    const listaTerm = (x, items, ok) => {
      let yy = py;
      for (const it of items) {
        doc.setFillColor(ok ? C.verde100 : C.rojoSuave); doc.circle(x + 2.2, yy + 1.4, 2.2, 'F');
        dibujarIcono(doc, ok ? 'check' : 'x', x + 0.6, yy - 0.2, 3.2, ok ? C.verde700 : C.rojo700);
        const yFinT = textoWrap(it, x + 7, yy + 2.4, colT - 9, 7, C.gray700);
        yy = Math.max(yy + 6.6, yFinT + 1.4);
      }
      return yy;
    };
    const yT1 = listaTerm(M, aplica, true);
    const yT2 = listaTerm(M + colT + 6, noAplica, false);
    py = Math.max(yT1, yT2) + 6;
    }

    // ACEPTACION (firma del CLIENTE)
    const acepH = 36;
    py = necesita(py, acepH + 4, headerPago, 22);
    doc.setDrawColor(C.blue); doc.setLineWidth(0.6); doc.setFillColor(C.blue50); doc.roundedRect(M, py, CW, acepH, 3, 3, 'FD');
    etiqueta('ACEPTACIÓN DE LA PROPUESTA', M + 6, py + 7.4, C.navy, 7);
    F('normal', 7.2, C.gray500); doc.text('Acepto la presente propuesta comercial y sus condiciones de pago y entrega.', M + 6, py + 12.6);
    const colA = (CW - 12 - 10) / 3;
    ['Nombre y puesto', 'Firma', 'Fecha'].forEach((t, i) => {
      const x0 = M + 6 + i * (colA + 5);
      doc.setDrawColor(C.gray400); doc.setLineWidth(0.3); doc.line(x0, py + 27.5, x0 + colA, py + 27.5);
      F('normal', 6.4, C.gray500); doc.text(t, x0, py + 31.6);
    });
  }

  // ============================================
  // SECCION 3 — CERTIFICADO DE GARANTIA
  // ============================================
  if (incluirCert) {
    doc.addPage(); marcarPagina('cert');
    const dipH = 60;
    degradado(0, 0, W, dipH, C.navy, C.blue);
    burbujas(0, 0, W, dipH);
    opacidad(() => { doc.setDrawColor(C.blanco); doc.setLineWidth(0.35); doc.roundedRect(6, 6, W - 12, dipH - 12, 2.5, 2.5, 'S'); }, 0.55);
    if (logoBlanco) { const hL = 9.5, wL = hL * (logoBlanco.w / logoBlanco.h); try { doc.addImage(logoBlanco.dataUrl, 'PNG', M, 11, wL, hL, 'beep-logo-blanco', 'FAST'); } catch { /* */ } }
    F('bold', 6.8, '#CFE2F5'); doc.text('DOCUMENTO OFICIAL', M, 27, { charSpace: 1.2 });
    F('black', 22, C.blanco); doc.text('Certificado de Garantía', M, 37.5);
    // fila de datos
    const inicioTxt = cert && cert.fecha_inicio ? fmtFecha(cert.fecha_inicio) : 'en la entrega';
    const datosFila = [
      ['FOLIO', cert && cert.folio ? cert.folio : 'Pendiente', true],
      ['CLIENTE', cliente.nombre || 'Sin cliente registrado', false],
      ['PROYECTO', cot.nombre || cot.folio || '—', false],
      ['INICIO', inicioTxt, false],
    ];
    const dfW = (W - M * 2 - 40) / 4;
    datosFila.forEach(([lab, val, mono], i) => {
      const x = M + i * dfW;
      etiqueta(lab, x, 46.5, '#9EC2E5', 5.6);
      if (mono) { MONO(true, 8.6, C.blanco); } else { F('bold', 7.4, C.blanco); }
      doc.text(doc.splitTextToSize(String(val), dfW - 4)[0], x, 51.5);
    });
    // SELLO integrado (arriba a la derecha, trazos blancos)
    const sx = W - M - 15, sy = 27, r = 13.5;
    opacidad(() => {
      doc.setDrawColor(C.blanco); doc.setLineWidth(0.8); doc.circle(sx, sy, r, 'S');
      doc.setLineWidth(0.3); doc.circle(sx, sy, r - 2.2, 'S');
      F('bold', 5.2, C.blanco); doc.text('GARANTÍA', sx, sy - 4.2, { align: 'center' }); doc.text('CERTIFICADA', sx, sy - 1.4, { align: 'center' });
      F('normal', 4.6, C.blanco); doc.text('Beep México', sx, sy + 2.2, { align: 'center' });
      F('bold', 4.6, C.blanco); doc.text(cert && cert.folio ? cert.folio : 'GAR-—', sx, sy + 5.6, { align: 'center' });
      doc.setLineWidth(0.3); doc.line(sx - 7.5, sy + 7.6, sx + 7.5, sy + 7.6);
    }, 0.92);

    let gy = dipH + 10;
    // Dos tarjetas de cobertura
    const prods = lineas.filter(l => !esServ(l));
    const mesesProd = prods.map(l => Number(l.garantia_meses) || 0).filter(Boolean);
    const minM = mesesProd.length ? Math.min(...mesesProd) : 0, maxM = mesesProd.length ? Math.max(...mesesProd) : 0;
    const rango = !mesesProd.length ? 'según fabricante' : (minM === maxM ? fmtGarantia(minM) : `de ${fmtGarantia(minM)} a ${fmtGarantia(maxM)}`);
    const mesesInst = Number(gcfg.garantia_instalacion_meses) || (lineas.find(l => esServ(l) && l.garantia_meses) || {}).garantia_meses || 12;
    const covW = (CW - 5) / 2, covH = 24;
    const covs = [
      ['EQUIPOS', 'Garantía de fabricante', `${prods.length} ${prods.length === 1 ? 'equipo' : 'equipos'} · ${rango}`, 'package', C.blue100, C.blue700],
      ['INSTALACIÓN BEEP', `${fmtGarantia(mesesInst)} de mano de obra`, 'diagnóstico y corrección sin costo', 'shield-check', C.verde100, C.verde700],
    ];
    covs.forEach(([lab, val, sub, ico, fill, col], i) => {
      const x = M + i * (covW + 5);
      doc.setFillColor(C.blanco); doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.roundedRect(x, gy, covW, covH, 2.6, 2.6, 'FD');
      doc.setFillColor(fill); doc.roundedRect(x + 4, gy + 5, 14, 14, 3, 3, 'F');
      dibujarIcono(doc, ico, x + 6.8, gy + 7.8, 8.4, col);
      etiqueta(lab, x + 22, gy + 8.2, col, 6.2);
      F('bold', 9, C.ink); doc.text(doc.splitTextToSize(val, covW - 26)[0], x + 22, gy + 14.2);
      F('normal', 6.6, C.gray500); doc.text(doc.splitTextToSize(sub, covW - 26)[0], x + 22, gy + 19.2);
    });
    gy += covH + 8;

    gy = tituloSeccion('EQUIPOS Y SERVICIOS AMPARADOS', gy);
    gy = tablaLineas(gy, 'garantia', headerCert) + 7;

    // QUE CUBRE
    const cubreTxt = gcfg.que_cubre || 'La garantía ampara defectos de fabricación de los equipos y la mano de obra de la instalación durante el periodo indicado.';
    gy = necesita(gy, 16 + alturaTexto(cubreTxt, CW - 10, 7.6), headerCert, 22);
    gy = tituloSeccion('QUÉ CUBRE', gy);
    doc.setFillColor(C.verdeSuave); doc.setDrawColor(C.verde100); doc.setLineWidth(0.3);
    const hCubre = alturaTexto(cubreTxt, CW - 10, 7.6) + 6;
    doc.roundedRect(M, gy - 1, CW, hCubre, 2.4, 2.4, 'FD');
    textoWrap(cubreTxt, M + 5, gy + 4, CW - 10, 7.6, '#1D4A33');
    gy += hCubre + 6;

    // CASOS NO CUBIERTOS: cuadricula compacta 4 x 2
    const casos = (Array.isArray(gcfg.casos) ? gcfg.casos : []).filter(c => c && c.activo !== false);
    const aperturaTxt = gcfg.apertura_no_cubiertos || 'Beep México no se hace responsable por daños derivados de las siguientes causas, ajenas a la instalación.';
    const cols = 4, cGap = 3, cW = (CW - cGap * (cols - 1)) / cols, cH = 21;
    const filas = Math.ceil(casos.length / cols);
    gy = necesita(gy, 14 + alturaTexto(aperturaTxt, CW, 7.2) + filas * (cH + cGap), headerCert, 22);
    gy = tituloSeccion('CASOS NO CUBIERTOS', gy);
    gy = textoWrap(aperturaTxt, M, gy, CW, 7.2, C.gray500) + 2;
    casos.forEach((c, i) => {
      const x = M + (i % cols) * (cW + cGap), yy = gy + Math.floor(i / cols) * (cH + cGap);
      doc.setFillColor(C.rojoSuave); doc.setDrawColor(C.rojoBorde); doc.setLineWidth(0.25); doc.roundedRect(x, yy, cW, cH, 2.4, 2.4, 'FD');
      doc.setFillColor(C.blanco); doc.circle(x + cW / 2, yy + 7, 4.6, 'F');
      dibujarIcono(doc, c.icono || 'default', x + cW / 2 - 2.8, yy + 4.2, 5.6, C.rojo700);
      F('bold', 6.2, C.rojo700);
      const t = doc.splitTextToSize(String(c.titulo || ''), cW - 4).slice(0, 2);
      doc.text(t, x + cW / 2, yy + 15.2, { align: 'center' });
    });
    gy += filas * (cH + cGap) + 3;

    // Recuadro ambar: visita de diagnostico
    const visitaTxt = gcfg.visita_diagnostico || 'Visita de diagnóstico: causa no cubierta → se cobra visita y servicio según tarifa; falla cubierta → sin costo.';
    const vh = alturaTexto(visitaTxt, CW - 24, 7.2) + 7;
    gy = necesita(gy, vh + 4, headerCert, 22);
    doc.setFillColor(C.ambarSuave); doc.setDrawColor(C.ambarBorde); doc.setLineWidth(0.3); doc.roundedRect(M, gy, CW, vh, 2.4, 2.4, 'FD');
    doc.setFillColor(C.blanco); doc.circle(M + 8, gy + vh / 2, 4.4, 'F'); dibujarIcono(doc, 'wrench', M + 5.2, gy + vh / 2 - 2.8, 5.6, C.ambar700);
    etiqueta('VISITA DE DIAGNÓSTICO', M + 16, gy + 5, C.ambar700, 6);
    textoWrap(visitaTxt, M + 16, gy + 9.4, CW - 24, 7.2, '#6B4A0A');
    gy += vh + 6;

    // Cierre: una sola tarjeta gris (codigo + leyenda + contacto + QR)
    const qrSize = 30, cierreH = 44;
    gy = necesita(gy, cierreH + 4, headerCert, 22);
    doc.setFillColor(C.gray50); doc.setDrawColor(C.gray200); doc.setLineWidth(0.3); doc.roundedRect(M, gy, CW, cierreH, 3, 3, 'FD');
    const txtW = CW - qrSize - 20;
    etiqueta('CÓDIGO DE VERIFICACIÓN', M + 6, gy + 7.4, C.gray400, 5.8);
    MONO(true, 12.5, C.ink); doc.text(cert && cert.codigo ? cert.codigo : 'Se genera al guardar', M + 6, gy + 14);
    F('normal', 6.6, C.gray500);
    const hostVer = datos.urlVerificacion ? String(datos.urlVerificacion).replace(/^https?:\/\//, '').replace(/\/[^/]*$/, '') : 'app.beepmexico.com/garantia';
    const ley = doc.splitTextToSize(`Verifica este certificado en ${hostVer} o escaneando el QR. El registro oficial vive en el sistema de Beep México; cualquier documento que difiera no es válido.`, txtW);
    doc.text(ley, M + 6, gy + 19.4);
    const contacto = gcfg.contacto || 'Para hacer válida su garantía comuníquese con Beep México indicando el folio de este certificado.';
    F('bold', 6.6, C.ink); doc.text('Para hacer válida tu garantía', M + 6, gy + 19.4 + ley.length * 3.4 + 2);
    F('normal', 6.6, C.gray700); doc.text(doc.splitTextToSize(contacto, txtW).slice(0, 3), M + 6, gy + 19.4 + ley.length * 3.4 + 5.6);
    // QR con marco blanco
    const qx = W - M - qrSize - 6, qy = gy + (cierreH - qrSize) / 2;
    doc.setFillColor(C.blanco); doc.setDrawColor(C.gray200); doc.roundedRect(qx - 2, qy - 2, qrSize + 4, qrSize + 4, 2.4, 2.4, 'FD');
    if (datos.qrDataUrl) { try { doc.addImage(datos.qrDataUrl, 'PNG', qx, qy, qrSize, qrSize, 'qr-garantia', 'FAST'); } catch { /* sin QR */ } }
    else { F('normal', 6, C.gray400); doc.text('QR al guardar', qx + qrSize / 2, qy + qrSize / 2, { align: 'center' }); }
  }

  // ---- Marca de agua (isotipo) + pie en todas las paginas ----
  const totalPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPaginas; i++) {
    doc.setPage(i);
    marcaAgua();
    dibujarFooter(i, totalPaginas, tiposPagina[i - 1] || 'cot');
  }

  const hoy = new Date();
  const stamp = `${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, '0')}${String(hoy.getDate()).padStart(2, '0')}`;
  const folioArchivo = (cot.folio || 'cotizacion').replace(/[^\w-]/g, '_');
  doc.save(`Cotizacion_${folioArchivo}_${stamp}.pdf`);
}

export default generarCotizacionPDF;
